import React from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const ProcessSection = () => {
  const scrollToApplication = () => {
    const element = document.getElementById('jetzt-bewerben');
    if (element) {
      element?.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const processSteps = [
    {
      step: 1,
      title: "Online-Bewerbung",
      description: "Füllen Sie unser Bewerbungsformular aus und laden Sie Ihre Unterlagen hoch",
      duration: "5 Minuten",
      icon: "FileText",
      color: "bg-primary",
      details: [
        "Persönliche Angaben eingeben",
        "Qualifikationen und Erfahrungen angeben",
        "Lebenslauf und Zeugnisse hochladen",
        "Motivationsschreiben verfassen"
      ]
    },
    {
      step: 2,
      title: "Erste Prüfung",
      description: "Unser HR-Team prüft Ihre Unterlagen und meldet sich innerhalb von 48 Stunden",
      duration: "1-2 Tage",
      icon: "Search",
      color: "bg-accent",
      details: [
        "Qualifikationsprüfung",
        "Vollständigkeitskontrolle",
        "Erste Bewertung",
        "Rückmeldung per E-Mail"
      ]
    },
    {
      step: 3,
      title: "Online-Interview",
      description: "Persönliches Gespräch mit unserem Recruiting-Team via Videocall",
      duration: "45 Minuten",
      icon: "Video",
      color: "bg-trust-builder",
      details: [
        "Kennenlernen und Motivation",
        "Fachliche Kompetenz prüfen",
        "Fragen zur Arbeitsweise",
        "Erwartungen besprechen"
      ]
    },
    {
      step: 4,
      title: "Vertragsangebot",
      description: "Bei positiver Bewertung erhalten Sie unser Vertragsangebot",
      duration: "1 Tag",
      icon: "FileCheck",
      color: "bg-success",
      details: [
        "Individuelles Vertragsangebot",
        "Vergütungsdetails",
        "Arbeitskonditionen",
        "Starttermin vereinbaren"
      ]
    }
  ];

  return (
    <section className="py-20 bg-surface">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-16">
          <h2 className="text-3xl lg:text-4xl font-bold text-text-primary mb-4">
            Ihr Weg zu AdvoCall
          </h2>
          <p className="text-xl text-text-secondary max-w-3xl mx-auto">
            Unser Bewerbungsprozess ist transparent, fair und darauf ausgelegt, die besten Anwälte für unser Team zu finden
          </p>
        </div>

        <div className="relative">
          {/* Process Timeline */}
          <div className="hidden lg:block absolute left-1/2 transform -translate-x-1/2 w-1 bg-slate-200 h-full"></div>
          
          <div className="space-y-12 lg:space-y-16">
            {processSteps?.map((step, index) => (
              <div
                key={step?.step}
                className={`relative flex flex-col lg:flex-row items-center gap-8 ${
                  index % 2 === 0 ? 'lg:flex-row' : 'lg:flex-row-reverse'
                }`}
              >
                {/* Step Number Circle */}
                <div className="hidden lg:block absolute left-1/2 transform -translate-x-1/2 z-10">
                  <div className={`w-16 h-16 ${step?.color} rounded-full flex items-center justify-center text-white font-bold text-xl shadow-elevated`}>
                    {step?.step}
                  </div>
                </div>

                {/* Content Card */}
                <div className={`flex-1 ${index % 2 === 0 ? 'lg:pr-12' : 'lg:pl-12'}`}>
                  <div className="bg-white rounded-2xl p-8 shadow-professional hover:shadow-elevated transition-shadow duration-300 card-hover">
                    <div className="flex items-center space-x-4 mb-6 lg:hidden">
                      <div className={`w-12 h-12 ${step?.color} rounded-full flex items-center justify-center`}>
                        <Icon name={step?.icon} size={24} color="white" />
                      </div>
                      <div className="text-2xl font-bold text-primary">
                        Schritt {step?.step}
                      </div>
                    </div>

                    <div className="hidden lg:flex items-center space-x-4 mb-6">
                      <div className={`w-14 h-14 ${step?.color} rounded-xl flex items-center justify-center`}>
                        <Icon name={step?.icon} size={28} color="white" />
                      </div>
                      <div className="flex items-center space-x-3">
                        <h3 className="text-2xl font-bold text-text-primary">
                          {step?.title}
                        </h3>
                        <span className="bg-slate-100 text-slate-600 px-3 py-1 rounded-full text-sm font-medium">
                          {step?.duration}
                        </span>
                      </div>
                    </div>

                    <div className="lg:hidden mb-4">
                      <h3 className="text-xl font-bold text-text-primary mb-2">
                        {step?.title}
                      </h3>
                      <span className="bg-slate-100 text-slate-600 px-3 py-1 rounded-full text-sm font-medium">
                        {step?.duration}
                      </span>
                    </div>

                    <p className="text-text-secondary mb-6 leading-relaxed">
                      {step?.description}
                    </p>

                    <div className="space-y-3">
                      <h4 className="font-semibold text-text-primary">Was Sie erwartet:</h4>
                      <ul className="space-y-2">
                        {step?.details?.map((detail, detailIndex) => (
                          <li key={detailIndex} className="flex items-start space-x-3">
                            <div className="w-5 h-5 bg-primary/10 rounded-full flex items-center justify-center mt-0.5 flex-shrink-0">
                              <Icon name="Check" size={12} color="var(--color-primary)" />
                            </div>
                            <span className="text-text-secondary text-sm">
                              {detail}
                            </span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  </div>
                </div>

                {/* Spacer for alternating layout */}
                <div className="hidden lg:block flex-1"></div>
              </div>
            ))}
          </div>
        </div>

        {/* Process Summary */}
        <div className="mt-20 bg-gradient-to-r from-primary to-accent rounded-2xl p-8 lg:p-12 text-white text-center">
          <div className="max-w-4xl mx-auto">
            <Icon name="Clock" size={48} color="white" className="mx-auto mb-6" />
            <h3 className="text-2xl lg:text-3xl font-bold mb-4">
              Gesamtdauer: 1-2 Wochen
            </h3>
            <p className="text-lg text-white/90 mb-8">
              Von der Bewerbung bis zum Vertragsangebot vergehen in der Regel nur 1-2 Wochen. Wir legen Wert auf einen schnellen und effizienten Prozess, damit Sie schnell bei uns starten können.
            </p>
            
            <div className="grid md:grid-cols-3 gap-6 mb-8">
              <div className="bg-white/10 backdrop-blur-sm rounded-lg p-4">
                <Icon name="Shield" size={32} color="white" className="mx-auto mb-3" />
                <h4 className="font-semibold mb-2">Transparent</h4>
                <p className="text-sm text-white/80">Klare Kommunikation in jedem Schritt</p>
              </div>
              <div className="bg-white/10 backdrop-blur-sm rounded-lg p-4">
                <Icon name="Zap" size={32} color="white" className="mx-auto mb-3" />
                <h4 className="font-semibold mb-2">Schnell</h4>
                <p className="text-sm text-white/80">Effiziente Abwicklung ohne Verzögerungen</p>
              </div>
              <div className="bg-white/10 backdrop-blur-sm rounded-lg p-4">
                <Icon name="Heart" size={32} color="white" className="mx-auto mb-3" />
                <h4 className="font-semibold mb-2">Fair</h4>
                <p className="text-sm text-white/80">Objektive Bewertung Ihrer Qualifikationen</p>
              </div>
            </div>

            {/* Call-to-Action Button */}
            <Button
              variant="default"
              size="lg"
              iconName="FileText"
              iconPosition="left"
              onClick={scrollToApplication}
              className="bg-white text-primary hover:bg-slate-100"
            >
              Jetzt bewerben
            </Button>
          </div>
        </div>
      </div>
    </section>
  );
};

export default ProcessSection;